/**
 * Мок деталей клиента для карточки (drawer). Достраивает `ClientListItem`
 * до `ClientDetail`: основное, сотрудники, платежи, заметки, активность.
 * Когда подключим BE — заменит `GET /admin/clients/{id}` (ADMIN_BACKEND §3.4).
 */

import type {ClientDetail, ClientListItem, StaffMember} from './types';

/** Правки мок-режима: мутации записывают сюда, buildClientDetail подмешивает.
    Так мок ведёт себя как живой бэк — после инвалидации приходят новые данные. */
const DETAIL_PATCHES = new Map<string, Partial<ClientDetail>>();

export const patchDetailMock = (id: string, patch: Partial<ClientDetail>): void => {
  DETAIL_PATCHES.set(id, {...DETAIL_PATCHES.get(id), ...patch});
};

const DOCTOR_POOL: {fullName: string; specialization: string}[] = [
  {fullName: 'Асанов Руслан', specialization: 'Ортодонт'},
  {fullName: 'Кыдырова Нургуль', specialization: 'Терапевт'},
  {fullName: 'Исаков Тилек', specialization: 'Хирург'},
  {fullName: 'Осмонова Динара', specialization: 'Гигиенист'},
  {fullName: 'Бекова Асель', specialization: 'Ортопед'},
];

const buildStaff = (c: ClientListItem): StaffMember[] => {
  if (c.type !== 'clinic') return [];
  const admin: StaffMember = {
    id: `${c.id}-admin`,
    accountId: `${c.id}-acc-admin`,
    fullName: c.adminFullName,
    phone: c.adminPhone,
    role: 'admin',
    specialization: null,
    active: true,
    blocked: false,
  };
  const doctors: StaffMember[] = DOCTOR_POOL.slice(0, Math.max(0, c.seatsUsed)).map((d, i) => ({
    id: `${c.id}-doc-${i}`,
    accountId: `${c.id}-acc-doc-${i}`,
    fullName: d.fullName,
    phone: `+996 555 ${100 + i}0 ${200 + i}0`,
    role: 'doctor',
    specialization: d.specialization,
    active: true,
    blocked: false,
  }));
  return [admin, ...doctors];
};

const buildPayments = (c: ClientListItem): ClientDetail['payments'] => {
  if (c.subscriptionStatus === 'trial' || c.subscriptionStatus === 'awaiting_card') return [];
  const amount = c.type === 'clinic' ? c.revenue : 1200;
  const failedFirst = c.subscriptionStatus === 'past_due';
  return [
    {
      id: `${c.id}-p1`,
      label: 'Продление · Июнь',
      date: '2026-06-28',
      amount,
      status: failedFirst ? 'failed' : 'success',
    },
    {id: `${c.id}-p2`, label: 'Продление · Май', date: '2026-05-28', amount, status: 'success'},
    {id: `${c.id}-p3`, label: 'Продление · Апрель', date: '2026-04-28', amount, status: 'success'},
  ];
};

export const buildClientDetail = (c: ClientListItem): ClientDetail => {
  const isClinic = c.type === 'clinic';
  const hasCard = c.subscriptionStatus !== 'trial' && c.subscriptionStatus !== 'awaiting_card';
  const notesEmpty = c.subscriptionStatus === 'trial' || c.subscriptionStatus === 'awaiting_card';
  return {
    id: c.id,
    address: isClinic ? 'ул. Ленина 42, оф. 3' : 'ул. Курманжан Датка 12',
    businessPhone: isClinic ? '+996 3722 5-11-90' : null,
    language: 'Русский',
    cardAttached: hasCard,
    specialization: isClinic ? null : 'Стоматолог-терапевт',
    activity: isClinic
      ? null
      : {
          patients: c.patientsCount,
          visits: Math.round(c.patientsCount * 0.4),
          lastLogin: 'сегодня 14:20',
        },
    staff: buildStaff(c),
    payments: buildPayments(c),
    notes: notesEmpty
      ? ''
      : 'Звонили по продлению — карта клиента временно заблокирована банком, обещали перепривязать. Повторить списание после подтверждения.',
    adminBlocked: false,
    whatsapp: null,
    aiMode: 'off',
    aiModeReason: null,
    aiModeChangedAt: null,
    ...DETAIL_PATCHES.get(c.id),
  };
};
